// The question pop-up (owner ask 2026-10-12: "when I go down to the questions
// and I click on one, it takes me over to Merlin's questions, which is not
// good. I want it to be a pop-up, and it should be a shared pop-up with the
// Merlin's questions tab"). One of Merlin's questions on its own, over
// whichever tab it was opened from: who is asked, the initiative it belongs
// to with its status and health, then the SAME card the Merlin's questions
// tab lists (QuestionCard: the question, the quoted words, the decide-by
// chip, Answer / Record / Dismiss). Prev / next walk the list it was opened
// from (the meeting view's "Needs an answer first", a person's list on the
// tab); Escape closes; under 640px it is a full-screen sheet.

import { useCallback, useEffect, useRef } from 'react';
import type { ClarifyingQuestion, InitiativeState } from '../../services/roadmapReports';
import { ownerLine, questionAddressees, STATUS_LABELS } from './roadmapViewUtils';
import { HealthChip } from './HealthChip';
import { QuestionCard } from './QuestionCard';
import type { OpenSourceFn } from './EvidenceList';
import type { RespondFn, WithdrawFn } from './QuestionResponseControls';
import type { MarkRelevanceFn } from './RelevanceControls';
import { useModalKeys } from './useModalKeys';
import tableStyles from '../RoadmapReportsPage.module.css';
import styles from './RoadmapV2.module.css';

/** Healths worth a chip beside the status (the schedule ones). */
const SHOWN_HEALTH: ReadonlySet<string> = new Set(['late', 'at_risk', 'stuck', 'on_track']);

export interface QuestionModalProps {
  question: ClarifyingQuestion;
  /** The initiative the question belongs to, when the page has it. */
  initiative?: InitiativeState | null;
  reportDate: string | null;
  onClose: () => void;
  /** Opens the initiative pop-up in place of this one. */
  onSelect: (key: string) => void;
  onRespond?: RespondFn;
  onWithdraw?: WithdrawFn;
  onOpenSource?: OpenSourceFn;
  /** Relevant / not relevant (kanban 3709db93). */
  onMark?: MarkRelevanceFn;
  /** Walk the list the question was opened from. */
  nav?: { index: number; total: number; onPrev: () => void; onNext: () => void } | null;
  /** Opens the Merlin's questions tab. */
  onOpenAll?: () => void;
  /** Injectable clock for tests. */
  today?: string;
}

export function QuestionModal({
  question: q,
  initiative = null,
  reportDate,
  onClose,
  onSelect,
  onRespond,
  onWithdraw,
  onOpenSource,
  onMark,
  nav = null,
  onOpenAll,
  today = new Date().toISOString().slice(0, 10),
}: QuestionModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const hasPrev = Boolean(nav && nav.index > 0);
  const hasNext = Boolean(nav && nav.index < nav.total - 1);
  const onKey = useCallback(
    (key: string) => {
      if (!nav) return false;
      if (key === 'ArrowLeft' && hasPrev) { nav.onPrev(); return true; }
      if (key === 'ArrowRight' && hasNext) { nav.onNext(); return true; }
      return false;
    },
    [nav, hasPrev, hasNext],
  );
  useModalKeys(dialogRef, onClose, onKey);

  // Focus the dialog when it opens and when prev / next swap the question.
  useEffect(() => {
    dialogRef.current?.focus();
  }, [q.initiative_key, q.question]);

  const who = questionAddressees(q).join(' / ');
  const title = initiative?.title ?? q.title;

  return (
    <>
      <div className={styles.modalBackdrop} onClick={onClose} data-testid="question-modal-backdrop" />
      <div
        ref={dialogRef}
        className={`${styles.modal} ${styles.questionModal}`}
        role="dialog"
        aria-modal="true"
        aria-label={`Merlin's question for ${who}`}
        tabIndex={-1}
        data-testid="question-modal"
      >
        <header className={styles.modalHead}>
          <div className={styles.modalHeadMain}>
            <div className={styles.questionModalKicker}>Merlin asks</div>
            <h2>{who}</h2>
            <div className={styles.chips}>
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() => onSelect(q.initiative_key)}
                title="Open the initiative this question belongs to"
                data-action="question_modal_open_initiative"
                data-target-initiative-key={q.initiative_key}
              >
                {title}
              </button>
              {initiative && (
                <>
                  <span className={`${tableStyles.statusChip} ${tableStyles[`status_${initiative.status}`] ?? ''}`}>
                    {STATUS_LABELS[initiative.status]}
                  </span>
                  {SHOWN_HEALTH.has(initiative.health) && <HealthChip health={initiative.health} initiative={initiative} />}
                  <span className={styles.meta}>{ownerLine(initiative)}</span>
                </>
              )}
            </div>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose} data-action="question_modal_close">Close</button>
        </header>

        <div className={styles.modalBody}>
          <QuestionCard
            q={q}
            today={today}
            reportDate={reportDate}
            sources={initiative?.sources ?? []}
            own
            as="div"
            showInitiative={false}
            onSelect={onSelect}
            onOpenSource={onOpenSource}
            onRespond={onRespond}
            onWithdraw={onWithdraw}
            onMark={onMark}
          />
          {initiative?.summary && (
            <div className={styles.questionModalContext} data-testid="question-modal-context">
              <div className={styles.evidenceSub}>About {title}</div>
              <p className={styles.summaryText}>{initiative.summary}</p>
            </div>
          )}
        </div>

        <footer className={styles.questionModalFoot}>
          {nav && nav.total > 1 ? (
            <div className={styles.questionModalNav}>
              <button
                type="button"
                className={styles.navBtn}
                onClick={nav.onPrev}
                disabled={!hasPrev}
                data-action="question_modal_prev"
                data-target-initiative-key={q.initiative_key}
              >
                &larr; Previous
              </button>
              <span className={styles.meta} data-testid="question-modal-pos">{nav.index + 1} of {nav.total}</span>
              <button
                type="button"
                className={styles.navBtn}
                onClick={nav.onNext}
                disabled={!hasNext}
                data-action="question_modal_next"
                data-target-initiative-key={q.initiative_key}
              >
                Next &rarr;
              </button>
            </div>
          ) : (
            <span />
          )}
          {onOpenAll && (
            <button type="button" className={styles.linkBtn} onClick={onOpenAll} data-action="question_modal_all">
              See all of Merlin's questions
            </button>
          )}
        </footer>
      </div>
    </>
  );
}
